import * as Sentry from '@sentry/vue'
import { isSentryLogMessage } from '@gto/sentry'

export const initSentry = (app, router) => {
  if (import.meta.env.VITE_ENV === 'production' && import.meta.env.VITE_BRANCH === 'master') {
    Sentry.init({
      app,
      maxValueLength: 5000,
      dsn: 'https://30a4a3ad2d004feea44d5adedea8a9e4@o446641.ingest.sentry.io/4504118442983424', // gwizteam
      integrations: [
        Sentry.browserTracingIntegration({
          router,
        }),
        Sentry.captureConsoleIntegration({ levels: ['debug'] }),
      ],
      tracesSampleRate: 0,
      ignoreErrors: [
        'TypeError: Illegal invocation', // FreshChat widget
        // TRAINER SOUND on some devices that have it disabled
        'NotSupportedError: Failed to load because no supported source was found.',
        // TRAINER SOUND error if user didnt interact with page first
        'NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',

        // Lazy load bundles handled by vite:preloadError
        /^Importing a module script failed/,
        /^Failed to fetch dynamically imported module/,
        /^Error loading dynamically imported module/,

        'Rendered items limit reached', // infinite scroller when someone turns off css height it fires thousands of events
        'Network Error',
        'Cannot redefine property: googletag',
        'Cannot set property chainId of #<D> which has only a getter',
        'Cannot set property chainId of #<J> which has only a getter', // some eth wallet extension
        'something went wrong TypeError: a.ethereum.initializeProvider is not a function',
        'something went wrong  TypeError: a.ethereum.initializeProvider is not a function',
        'Non-Error promise rejection captured with keys: currentTarget, isTrusted, target, type', // something with PWA reload on /coaching
        `Cannot read properties of undefined (reading 'outerHTML')`, // some error from unknown browser
        'ResizeObserver loop limit exceeded', // ResizeObserver
        'ResizeObserver loop completed with undelivered notifications.', // ResizeObserver
        'Object.keys called on non-object', // FB
        'Detected container element removed from DOM', // PayPal BTN
        'Can not send postrobot_method. Target window is closed', // PayPal BTN
        'Avoided redundant navigation to current location', // Vue Router
        `Can't find variable: instantSearchSDKJSBridgeClearHighlight`, // Bing
        `undefined is not an object (evaluating 'document.getElementsByTagName('video')[0].webkitExitFullScreen')`, // YouTube
        'e.$refs.content.contains', // Vue youtube
        'this.player.setSize is not a function', // Vue youtube
        `Cannot read properties of null (reading 'CodeMirror')`, // CodeMirror
        `Cannot read properties of undefined (reading 'firefoxSample')`, // Chrome extension 'OrangeMonkey'.
        'XDEFI: Failed to start XDEFIWeb3Provider: TypeError: Cannot redefine property: solana',
        'Disconnected from OKX Wallet background. Page reload required',

        // --- RECOMENDED LIST
        // https://gist.github.com/Chocksy/e9b2cdd4afc2aadc7989762c4b8b495a
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        `Can't find variable: ZiteReader`,
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // Avast extension error
        '_avast_submit',

        // Some random error from some extension most likely triggering 1000x times
        // https://gtowizard.sentry.io/issues/5155228321/?project=4504118442983424&query=is%3Aunresolved&referrer=issue-stream&sort=freq&statsPeriod=14d&stream_index=0
        'null has no properties',

        // Vue transition error
        // https://gtowizard.sentry.io/issues/5110014700/?project=4504118442983424&query=is%3Aunresolved&referrer=issue-stream&sort=freq&statsPeriod=14d&stream_index=3
        'S.el.getBoundingClientRect is not a function',
      ],
      denyUrls: [
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ],
      beforeSend: event => {
        // Don't pollute Sentry with debug messages not related to our app
        if (event.level === 'debug' && !isSentryLogMessage(event.message)) {
          return null
        }

        const exception = event.exception?.values?.[0]
        const frames = exception?.stacktrace?.frames
        const message = exception?.value

        if (frames) {
          const snippetFrame = frames[frames.length - 2] // second to last frame, last frame is native code

          // silence freshchat errors which are caused by it trying to parse postMessage which is sometimes sent from various extensions
          // https://gtowizard.sentry.io/issues/6547693984/events/cfb6f09124e040f8b371b201eb2f6afc/?project=4504118442983424
          if (
            snippetFrame?.abs_path?.includes('freshchat.com/js/widget.js') &&
            (message?.includes('JSON Parse error: Unexpected identifier "object"') ||
              message?.includes('"[object Object]" is not valid JSON'))
          ) {
            return null
          }
        }

        return event
      },
    })
  }
}

export const setSentryUser = (user: { email: string }) => {
  Sentry.setUser(user)
}
